/**
 * SharedDiagnosticPage.js
 * ========================
 * Page publique (sans login) pour consulter un Diagnostic IA partagé.
 * Le manager génère un lien token et le forward à son équipe.
 * Lecture seule, expire après 7 jours.
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    Sparkles, TrendingUp, ArrowRight, Info, CheckCircle2,
    AlertCircle, AlertTriangle, FileWarning, TrendingDown, Activity,
    Clock, Phone, RefreshCw, Target, Home, FileSignature, Euro, Loader2, Eye
} from 'lucide-react';

const LEVER_ICONS = {
    first_contact_delay_h: Clock,
    five_min_rule_pct: Sparkles,
    reachability_pct: Phone,
    estim_followup_pct: RefreshCw,
    signing_conversion_pct: Target,
    visit_to_offer_pct: Home,
    first_offer_accepted_pct: CheckCircle2,
    offer_discount_pct: Euro,
    compromis_fallthrough_pct: FileSignature,
};
const SIGNAL_ICONS = { AlertTriangle, FileWarning, TrendingDown, Activity, AlertCircle };

function fmtEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}
function fmtValue(key, v) {
    if (v === null || v === undefined) return '—';
    if (key === 'first_contact_delay_h') return `${Number(v).toFixed(2)} h`;
    return `${Number(v).toFixed(v % 1 === 0 ? 0 : 1)} %`;
}

function SignalCard({ signal }) {
    const Icon = SIGNAL_ICONS[signal.icon] || AlertTriangle;
    const sev = signal.severity;
    const styles = sev === 3
        ? { bg: 'bg-rose-50', border: 'border-rose-300', iconBg: 'bg-rose-200 text-rose-700', label: 'CRITIQUE' }
        : sev === 2
            ? { bg: 'bg-amber-50', border: 'border-amber-300', iconBg: 'bg-amber-200 text-amber-700', label: 'IMPORTANT' }
            : { bg: 'bg-blue-50', border: 'border-blue-200', iconBg: 'bg-blue-100 text-blue-700', label: 'INFO' };
    return (
        <div className={`rounded-xl border-2 ${styles.border} ${styles.bg} p-4 flex gap-3`} data-testid={`shared-signal-${signal.kpi}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold tracking-wider ${
                        sev === 3 ? 'text-rose-700' : sev === 2 ? 'text-amber-700' : 'text-blue-700'
                    }`}>{styles.label}</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-sm font-semibold text-slate-900">{signal.label}</span>
                </div>
                <div className="text-sm text-slate-800 font-medium mb-1">{signal.message}</div>
                <div className="text-xs text-slate-600 italic flex items-start gap-1">
                    <ArrowRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    {signal.action}
                </div>
            </div>
        </div>
    );
}

function InsightCard({ insight, rank }) {
    const Icon = LEVER_ICONS[insight.lever_key] || Target;
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.08 }}
            className="bg-white rounded-2xl border border-slate-200 p-6"
            data-testid={`shared-insight-${insight.lever_key}`}
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white shadow-md">
                    <Icon className="w-6 h-6" />
                </div>
                <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-purple-700">Levier #{rank}</div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">{insight.lever_label}</h3>
                </div>
            </div>
            <div className="flex items-center gap-3 mb-5 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Actuel</div>
                    <div className="text-xl font-semibold text-slate-900 tabular-nums">
                        {fmtValue(insight.lever_key, insight.current_value)}
                    </div>
                </div>
                <ArrowRight className="w-5 h-5 text-purple-500 flex-shrink-0" />
                <div className="flex-1 text-right">
                    <div className="text-[10px] uppercase tracking-wider text-emerald-600 mb-0.5">Cible · Top 20%</div>
                    <div className="text-xl font-semibold text-emerald-700 tabular-nums">{insight.target_label}</div>
                </div>
            </div>
            <div className="flex items-baseline justify-between mb-3">
                <span className="text-sm text-slate-500">Gain estimé / an</span>
                <span className="text-2xl font-bold text-emerald-600 tabular-nums">+{fmtEUR(insight.ca_yearly_gain)}</span>
            </div>
            <div className="text-sm text-slate-700 leading-relaxed border-t border-slate-100 pt-3">
                <span className="font-medium">Action : </span>{insight.action}
            </div>
        </motion.div>
    );
}

export default function SharedDiagnosticPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`/api/public/diagnostic/${token}`);
                if (res.status === 410) {
                    setError('Ce lien a expiré (validité : 7 jours).');
                    return;
                }
                if (res.status === 404) {
                    setError('Ce lien est invalide ou a été révoqué.');
                    return;
                }
                if (!res.ok) {
                    setError('Erreur de chargement du diagnostic.');
                    return;
                }
                const json = await res.json();
                setData(json);
            } catch (e) {
                setError('Impossible de charger le diagnostic.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto mb-3" />
                    <div className="text-slate-600">Chargement du diagnostic…</div>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
                <div className="max-w-md bg-white border border-rose-200 rounded-2xl p-6 text-center">
                    <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
                    <h2 className="text-lg font-bold text-slate-900 mb-2">Lien indisponible</h2>
                    <p className="text-sm text-slate-600">{error || 'Erreur inconnue'}</p>
                    <a href="https://homly.pro" className="inline-block mt-4 text-sm text-purple-700 font-medium">
                        Découvrir Homly →
                    </a>
                </div>
            </div>
        );
    }

    const diag = data.diagnostic || {};
    const combinedYearly = diag?.combined?.ca_yearly_gain || 0;
    const combinedMonthly = diag?.combined?.ca_monthly_gain || 0;
    const insights = diag.top_insights || [];
    const signals = diag.critical_signals || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-slate-50 py-6 md:py-10">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6" data-testid="shared-diagnostic">
                {/* Brand header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-purple-800">Homly.</span>
                        <span className="text-xs text-slate-500 hidden sm:inline">· Diagnostic partagé</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Eye className="w-3 h-3" />
                        <span>{data.view_count} vue{data.view_count > 1 ? 's' : ''}</span>
                    </div>
                </div>

                {/* Meta bar */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-center justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">Agence</div>
                        <div className="font-bold text-slate-900">{data.agency_name}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">Partagé par</div>
                        <div className="font-medium text-slate-700">{data.shared_by_name}</div>
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-wider text-slate-500">Expire</div>
                        <div className="text-sm text-slate-700">
                            {new Date(data.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </div>
                    </div>
                </div>

                {/* Hero */}
                {insights.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="relative overflow-hidden rounded-2xl p-6 md:p-8 mb-6 bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900 text-white shadow-xl"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-pink-500/20 to-transparent rounded-full blur-3xl -mr-32 -mt-32" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-emerald-400/20 to-transparent rounded-full blur-3xl -ml-32 -mb-32" />
                        <div className="relative">
                            <div className="text-xs uppercase tracking-widest text-purple-200 mb-2 flex items-center gap-2">
                                <TrendingUp className="w-3.5 h-3.5" />
                                Opportunité détectée
                            </div>
                            <div className="text-4xl md:text-5xl font-bold tabular-nums mb-2">
                                +{fmtEUR(combinedYearly)}<span className="text-xl md:text-2xl font-normal text-purple-200 ml-2">/ an</span>
                            </div>
                            <div className="text-sm text-purple-100 mb-4">soit +{fmtEUR(combinedMonthly)} en moyenne / mois</div>
                            {diag.ai_narrative && (
                                <div className="text-base md:text-lg italic leading-relaxed text-white/95 border-l-2 border-purple-300 pl-4">
                                    « {diag.ai_narrative} »
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {/* Critical signals */}
                {signals.length > 0 && (
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle className="w-4 h-4 text-rose-600" />
                            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">
                                Signaux d'urgence opérationnels
                            </h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {signals.map((s, i) => <SignalCard key={i} signal={s} />)}
                        </div>
                    </div>
                )}

                {/* Insights */}
                {insights.length > 0 && (
                    <>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-px flex-1 bg-slate-200" />
                            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                Vos 3 leviers prioritaires
                            </span>
                            <div className="h-px flex-1 bg-slate-200" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
                            {insights.map((ins, idx) => (
                                <InsightCard key={ins.lever_key} insight={ins} rank={idx + 1} />
                            ))}
                        </div>
                    </>
                )}

                {/* Footer */}
                <div className="text-center mt-10 mb-4">
                    <p className="text-xs text-slate-500 mb-2">
                        Diagnostic généré par <a href="https://homly.pro" className="text-purple-700 font-medium">Homly</a> ·
                        Plateforme SaaS pour agences immobilières
                    </p>
                    <p className="text-[11px] text-slate-400">
                        Lien valide 7 jours · Lecture seule · Données réelles snapshotées au moment du partage
                    </p>
                </div>
            </div>
        </div>
    );
}
